import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import isLogged from 'utils/isLogged';

type Props = {
  children: ReactNode;
};

export type CardType = {
  number: string;
  id: number;
};

type ActiveCardContextType = {
  activeCard: CardType | undefined | null;
  fetchActiveCard: () => Promise<CardType | undefined>;
  fetchingCard: boolean;
};

const ActiveCardContext = createContext<ActiveCardContextType>(
  {} as ActiveCardContextType,
);

const useActiveCard = (): ActiveCardContextType =>
  useContext(ActiveCardContext);

const ActiveCardProvider = ({ children }: Props): JSX.Element => {
  const location = useLocation();
  const [activeCard, setActiveCard] = useState<CardType | undefined | null>();
  const [fetchingCard, setFetchingCard] = useState<boolean>(false);

  const isClientLogged = isLogged();

  const fetchActiveCard = useCallback(async (): Promise<
    CardType | undefined
  > => {
    try {
      setFetchingCard(true);
      const fetchedActiveCard = await axiosInstance.get('/client/cards/active');
      setActiveCard(fetchedActiveCard.data);
      return fetchedActiveCard.data;
    } catch {
      setActiveCard(null);
      return undefined;
    } finally {
      setFetchingCard(false);
    }
  }, []);

  useEffect(() => {
    const prefetch = async () => {
      if (activeCard === undefined && isClientLogged) {
        await fetchActiveCard();
      }
    };
    prefetch();
  }, [isClientLogged, location]);

  const activeCardContextValue = useMemo(
    () => ({
      activeCard,
      fetchActiveCard,
      fetchingCard,
    }),
    [activeCard, fetchingCard, fetchActiveCard],
  );

  return (
    <ActiveCardContext.Provider value={activeCardContextValue}>
      {children}
    </ActiveCardContext.Provider>
  );
};

export { useActiveCard as default, ActiveCardProvider };
